import React, { useMemo, useReducer } from 'react'

import _find from 'lodash/find'
import { toast } from 'react-toastify'

import { DocsTemplatesEdit } from './steps/DocsTemplatesEdit'
import { DocsTemplatesPreview } from './steps/DocsTemplatesPreview'
import { ESignaturePreview } from './steps/ESignaturePreview'
import { MsgTemplateEdit } from './steps/MsgTemplateEdit'
import { MsgTemplatePreview } from './steps/MsgTemplatePreview'
import { RecipientDocumentsSelect } from './steps/RecipientDocumentsSelect'
import { StepSelectMessageType } from './steps/SelectMessageType'
import { Drawer } from '@/components/Drawer/DrawerV2'
import Stepper from '@/components/Stepper'
import { ESignatureRecipients } from '@/features/SidePanel/CustomerMessages/steps/ESignatureRecipients'
import { useCurrentApplication } from '@/graphql/services/application'
import { useGetDealTeamsByApplicationQuery } from '@/services/application'
import { useGetApplicationOffersQuery } from '@/services/applicationCalcs'
import { useListDocumentTemplatesQuery } from '@/services/documentTemplate'
import {
  useDocumentUploadFinishMutation,
  useDocumentUploadStartMutation,
} from '@/services/documents'
import { useCreateMessageMutation, useCreateRequestEsignatureMutation } from '@/services/message'
import { useCreateDocRequestMutation } from '@/services/multiDocumentsRequest'
import { useUploadFileMutation } from '@/services/s3'
import { useGetLiensQuery } from '@/services/tradelines'
import { useGetLoggedUserQuery } from '@/services/user'
import { useListApplicationPartiesQuery } from '@/services/v2-application/applicationParties'
import { useListApplicationProfilesQuery } from '@/services/v2-application/applicationProfile'
import { UserDocument } from '@/types/unlock'

type NewCustomerMessageProps = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  initialMsgType?: 'request-information' | 'request-multi-document' | 'request-esignature'
  document?: UserDocument | null
}

export function NewCustomerMessage({
  isOpen,
  setIsOpen,
  initialMsgType,
  document,
}: NewCustomerMessageProps) {
  const { data: application } = useCurrentApplication()
  const [isTransferingFiles, setIsTransferingFiles] = React.useState(false)

  const { data: docTemplates, isLoading: isLoadingDocTemplates } = useListDocumentTemplatesQuery({
    is_requested_document: true,
  })

  const { data: applicationProfiles } = useListApplicationProfilesQuery(application?.uuid, {
    skip: !application?.uuid,
  })

  const applicantProfile = _find(applicationProfiles?.results, { position: 1 })

  const { data: applicationParties } = useListApplicationPartiesQuery(application?.uuid, {
    skip: !application?.uuid,
  })

  const { data: user } = useGetLoggedUserQuery()

  const { data: dealTeams } = useGetDealTeamsByApplicationQuery(application?.id, {
    skip: !application?.id,
  })

  const salesUser = dealTeams?.results?.find((it) => it.type === 'sales')

  const getApplicationParty = (type) =>
    applicationParties?.results.find((it) => it.application_party_type === type)?.party

  const { data: applicationOffer } = useGetApplicationOffersQuery(application?.uuid, {
    skip: !application?.uuid,
  })

  const hasOffer = !!applicationOffer?.selected_offer?.offers

  const { data: liens } = useGetLiensQuery(application?.uuid, {
    skip: !application?.uuid,
  })

  const [getPreSignedUrl] = useDocumentUploadStartMutation()
  const [transferFileToBucket] = useUploadFileMutation()
  const [documentUploadFinish] = useDocumentUploadFinishMutation()

  const initialState = {
    msgType: initialMsgType,
    msgTemplate: '',
    documents: [],
    docMessages: {},
    eSignature: {},
    eSignatures: [],
    msgTemplateType: '',
    attachments: [],
    recipients: [],
    recipientDocuments: {},
  }

  function reducer(state, action) {
    switch (action.type) {
      case 'setMsgType':
        return {
          msgType: action.payload,
          msgTemplate: '',
          documents: [],
          eSignature: {},
          msgTemplateType: '',
          recipients: [],
          recipientDocuments: {},
        }
      case 'setMsgTemplate':
        return {
          ...state,
          msgType: state.msgType,
          msgTemplate: action.payload,
          documents: [],
          eSignature: {},
          attachments: [],
          recipients: [],
          recipientDocuments: {},
        }
      case 'setDocuments':
        return {
          ...state,
          documents: action.payload,
        }
      case 'setDocMessages':
        return {
          ...state,
          docMessages: {
            ...state.docMessages,
            [action.payload.recipientId]: {
              ...state.docMessages?.[action.payload.recipientId],
              [action.payload.docType]: action.payload.message,
            },
          },
        }
      case 'setRecipients':
        return {
          ...state,
          recipients: action.payload.recipients,
        }
      case 'setRecipientDocuments':
        return {
          ...state,
          recipientDocuments: {
            ...state.recipientDocuments,
            [action.payload.recipientId]: action.payload.documents,
          },
        }
      case 'setESignatures': {
        const { applicationProfileId } = action.payload

        if (action?.payload?.action === 'remove')
          return {
            ...state,
            eSignatures: [
              // Remove any existing eSignature for the same application profile to prevent duplicates
              ...(state.eSignatures?.filter(
                (e) => e?.applicationProfileId !== applicationProfileId
              ) || []),
            ],
          }

        return {
          ...state,
          eSignatures: [
            // Remove any existing eSignature for the same application profile to prevent duplicates
            ...(state.eSignatures?.filter(
              (e) => e?.applicationProfileId !== applicationProfileId
            ) || []),

            // Append the new or updated eSignature to the list
            action.payload,
          ],
        }
      }
      case 'setMsgTemplateType':
        return {
          ...state,
          msgTemplateType: action.payload,
        }
      case 'setMsgAttachments':
        return {
          ...state,
          attachments: action.payload.attachments,
        }
      case 'removeAttachment':
        return {
          ...state,
          attachments: state.attachments.filter((item) => item.name !== action.payload),
        }
      case 'reset': {
        return initialState
      }
      default:
        return state
    }
  }

  const [state, dispatch] = useReducer(reducer, initialState)

  const initialStepIndex = initialMsgType ? 1 : 0

  // To be correctly replaced, these values should match the ones in /utils/templateVarsObj
  const templateVars = {
    applicantFullName: applicantProfile?.profile?.full_name,
    coApplicantFullName: applicationProfiles?.results.find((it) => it.position === 2)?.profile
      ?.full_name,
    coOwnerFullName: getApplicationParty('co_owner')?.contact_name,
    nooFullName: getApplicationParty('non_owning_occupant')?.contact_name,
    tsFullName: user?.first_name + ' ' + user?.last_name,
    tsPhone: user?.data?.phone,
    tsEmail: user?.email,
    hecFullName: salesUser?.full_name,
    hecPhone: salesUser?.phone,
    hecEmail: salesUser?.email,
  }

  const requestEsignatureIsInvalid = useMemo(() => {
    const { eSignatures, recipients } = state
    return !recipients?.every((recipient) =>
      eSignatures?.some((eSignature) => recipient?.id === eSignature?.applicationProfileId)
    )
  }, [state.eSignatures, state.recipients])

  const documentsRequestIsInvalid = useMemo(() => {
    const { recipients, recipientDocuments } = state

    if (!recipients?.length) return true

    // Check if all recipients has at least one document selected
    return !recipients.every((recipient) => recipientDocuments[recipient.id]?.length > 0)
  }, [state.recipients, state.recipientDocuments])

  const templatedMessageFlow = [
    {
      title: 'Select and Customize Message Template',
      content: () => (
        <MsgTemplateEdit
          dispatch={dispatch}
          templateVars={templateVars}
        />
      ),
      buttonLabel: 'Next',
      disabledButton: !state.msgTemplate,
    },
    {
      title: 'Preview',
      content: () => (
        <MsgTemplatePreview
          state={state}
          dispatch={dispatch}
        />
      ),
      buttonLabel: '',
      hideButton: true,
      disabledStep: !state.msgTemplate,
    },
  ]

  const documentsFlow = [
    {
      title: 'Recipient(s) & Document(s) to request',
      content: () => (
        <RecipientDocumentsSelect
          dispatch={dispatch}
          application={application}
          docTemplates={docTemplates}
          isLoadingDocTemplates={isLoadingDocTemplates}
        />
      ),
      buttonLabel: 'Next',
      disabledButton: documentsRequestIsInvalid,
    },
    {
      title: 'Customize Template(s)',
      content: () => (
        <DocsTemplatesEdit
          dispatch={dispatch}
          docTemplates={docTemplates}
          recipients={state.recipients}
          recipientDocuments={state.recipientDocuments}
          liens={liens?.results ?? []}
        />
      ),
      buttonLabel: 'Next',
      disabledStep: documentsRequestIsInvalid,
    },
    {
      title: 'Preview',
      content: () => <DocsTemplatesPreview state={state} />,
      buttonLabel: '',
      hideButton: true,
      disabledStep: documentsRequestIsInvalid,
    },
  ]

  const eSignatureFlow = [
    {
      title: 'Recipient(s) & Document(s) to eSign',
      content: () => (
        <ESignatureRecipients
          primaryApplicantId={applicantProfile?.id}
          state={state}
          dispatch={dispatch}
          application={application}
          document={document}
        />
      ),
      disabledButton: requestEsignatureIsInvalid,
      buttonLabel: 'Next',
    },
    {
      title: 'Preview',
      content: () => <ESignaturePreview state={state} />,
      buttonLabel: 'Next',
      hideButton: true,
      disabledStep: !state.eSignatures?.length,
    },
  ]

  const messageFlowLookup = {
    'request-information': templatedMessageFlow,
    'request-multi-document': documentsFlow,
    'request-esignature': eSignatureFlow,
  }

  const steps = [
    {
      title: 'Request type',
      content: () => (
        <StepSelectMessageType
          dispatch={dispatch}
          selectedValue={state.msgType}
          hasOffer={hasOffer}
        />
      ),
      buttonLabel: 'Next',
      disabledButton: !state.msgType,
      disabledStep: !!initialMsgType,
    },
    ...(messageFlowLookup?.[state.msgType] ?? []),
  ]

  const [createMessage, { isLoading: isCreatingMessage }] = useCreateMessageMutation()
  const [createDocRequest, { isLoading: isCreatingDocRequest }] = useCreateDocRequestMutation()
  const [createRequestEsignature] = useCreateRequestEsignatureMutation()

  const sendRequestInformation = async () => {
    const formData = new FormData()

    formData.append('type', 'text')
    formData.append('body', state.msgTemplate)
    formData.append('template_type', state.msgTemplateType)

    if (state.attachments?.length > 0) {
      setIsTransferingFiles(true)
      const fileTransferPromises = state?.attachments?.map(async (file) => {
        const resp = await getPreSignedUrl({
          file_name: file.name,
          application_profile: applicantProfile?.id,
          customer_visible: true,
          kind: 'other--generic',
          source: 'max-system',
          tag: 'Deal Files/CX Docs',
        }).unwrap()

        await transferFileToBucket({ url: resp.url, file })
        return await documentUploadFinish(resp.uuid).unwrap()
      })
      const fileTransferResponses = await Promise.all(fileTransferPromises)
      setIsTransferingFiles(false)
      formData.append('attachments', JSON.stringify(fileTransferResponses.map((item) => item.uuid)))
    }

    formData.append('application', application.id?.toString())
    formData.append('to', applicantProfile?.profile?.user?.toString())
    formData.append('to_profile', applicantProfile?.profile?.id?.toString())
    await createMessage(formData)
  }

  const createRequestEsignatureFormData = (eSignature) => {
    const formData = new FormData()

    const { profile } = state.recipients.find(
      (recipient) => recipient.id === eSignature?.applicationProfileId
    )

    formData.append('type', 'request-esignature')
    formData.append('body', eSignature.message)
    formData.append('request_esignature', eSignature.doc)
    formData.append('application', application.id?.toString())
    formData.append('to_profile', profile.id?.toString())

    return formData
  }

  const sendRequestEsignature = async () => {
    const requestEsignaturesPromises = state.eSignatures.map((eSignature) => {
      const formData = createRequestEsignatureFormData(eSignature)
      return createRequestEsignature(formData)
    })

    await Promise.all(requestEsignaturesPromises)
  }

  const sendDocumentRequests = async () => {
    const { recipients, recipientDocuments, docMessages } = state

    // Create document requests for each selected recipient with their selected documents
    const documentRequestPromises = recipients
      .map((recipient) => {
        const recipientDocs = recipientDocuments[recipient.id] || []

        // Skip recipients with no documents selected
        if (recipientDocs.length === 0) return Promise.resolve()

        return createDocRequest({
          application: application.id,
          to: recipient.profile?.user?.id,
          to_profile: recipient.profile?.id,
          messages: recipientDocs.map((doc) => ({
            requested_document: null,
            requested_document_template: doc.id || doc.value,
            body:
              docMessages?.[recipient.id]?.[doc.id || doc.value] ||
              doc.request_template_message ||
              '',
            template_type: doc.name,
          })),
        })
      })
      .filter(Boolean) // Remove any undefined promises (from skipped recipients)

    await Promise.all(documentRequestPromises)
  }

  const submitNotification = () => {
    toast.success(
      <div>
        <p>Message Sent!</p>
        <p className='text-sm mt-1 text-gray-600'>
          You will be notified when the customer has read this message.
        </p>
      </div>,
      {
        hideProgressBar: true,
        autoClose: 3000,
      }
    )
    dispatch({ type: 'reset' })
    setIsOpen(false)
  }

  const handleFinish = async () => {
    if (state.msgType === 'request-esignature') {
      await sendRequestEsignature()
      submitNotification()
    } else if (state.msgType === 'request-information') {
      await sendRequestInformation()
      submitNotification()
    } else if (state.msgType === 'request-multi-document') {
      await sendDocumentRequests()
      submitNotification()
    }
  }

  return (
    <>
      <Drawer
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        title='Workflow / Customer Message'
        confirmBeforeClose={state.msgType !== ''} // confirm before closing only after the first step
      >
        <Stepper
          steps={steps}
          initialStep={initialStepIndex}
          finishButton={{
            label: 'Send Message to Customer',
            onClick: handleFinish,
            extraDisabled: isCreatingMessage || isCreatingDocRequest || isTransferingFiles,
          }}
        />
      </Drawer>
    </>
  )
}
